/**
 * Main WarehouseTable Component
 * 
 * A complete, reusable table component with all the professional styling
 * and functionality from the ItemLocationBalance table, but generic and
 * type-safe for any data type.
 */

import React, { useEffect, useCallback } from 'react';
import DateFilterInput from './DateFilterInput';
import {
  Search,
  Download,
  RefreshCw,
  Settings,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  AlertCircle,
  Pencil,
  Save,
  X,
  Printer,
} from 'lucide-react';
import { useWarehouseTable } from '../hooks/useWarehouseTable';
import { ResizableHeader } from './ResizableHeader';
import { ColumnDropdownFilter } from './ColumnDropdownFilter';
import { useAuth } from '../../../../contexts/AuthContext';
import { printingService } from '../../../../services/printingService';
import type { TablePdfColumn } from '../../../../types/printing';
import { ColumnConfigModal } from './ColumnConfigModal';
import { SelectionCheckbox } from './SelectionCheckbox';
import { ExpandableDetailTable } from './ExpandableDetailTable';
import { defaultFormatter } from '../utils/formatters';
import { getDistinctValues } from '../utils/tableUtils';
import type { WarehouseTableProps, TableDataRow, ColumnDefinition, InlineEditConfig, SortDirection } from '../types/table';
import '../styles/warehouse-table.css';

export function WarehouseTable<T extends TableDataRow>({
  data,
  columns: initialColumns,
  config = {},
  selectedRows,
  onSelectionChange,
  getRowId,
  actions = {},
  rowActions = [],
  tableActions = [],
  summaryCalculations = [],
  loading = false,
  error,
  className = '',
  'aria-label': ariaLabel = 'Data table',
  expandableConfig,
  inlineEditConfig,
  toolbarLeft,
  serverPagination,
  dropdownOptionsData,
  enableDropdownFilters = false
}: WarehouseTableProps<T>) {
  
  const {
    tableState,
    handleSort,
    handleFilter,
    handlePageChange,
    handlePageSizeChange,
    handleColumnToggle,
    handleColumnReorder,
    handleColumnResize,
    handleExport,
    handleReset,
    handleRowSelect,
    handleSelectAll,
    handleSelectionClear,
    summaryData,
    visibleColumns,
    expandedRows,
    handleToggleExpand
  } = useWarehouseTable({
    data,
    columns: initialColumns,
    config,
    summaryCalculations,
    selectedRows,
    onSelectionChange,
    getRowId,
    expandableConfig,
    serverPagination
  });

  const [showColumnConfig, setShowColumnConfig] = React.useState(false);
  const [isPrinting, setIsPrinting] = React.useState(false);
  const { user } = useAuth();
  const companyNo = user?.companyNo || '';

  // Handle PDF table print
  const handlePrint = useCallback(async () => {
    if (!companyNo || isPrinting) return;
    setIsPrinting(true);
    try {
      const pdfColumns: TablePdfColumn[] = visibleColumns.map(col => ({
        key: String(col.key),
        label: col.label,
        alignment: col.alignment || 'left',
        relativeWidth: col.width,
      }));

      const pdfRows = tableState.filteredData.map(row => {
        const mapped: Record<string, any> = {};
        for (const col of visibleColumns) {
          const val = row[col.key];
          mapped[String(col.key)] = col.formatter ? col.formatter(val, row) : val;
        }
        return mapped;
      });

      const title = config.persistenceKey
        ? config.persistenceKey.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        : 'Tabellutskrift';

      const blob = await printingService.exportTableAsPdf(companyNo, {
        title,
        isLandscape: true,
        columns: pdfColumns,
        rows: pdfRows,
      });

      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error('Table print failed:', err);
    } finally {
      setIsPrinting(false);
    }
  }, [companyNo, isPrinting, visibleColumns, tableState.filteredData, config.persistenceKey]);

  // Determine if inline editing is active
  const hasInlineEdit = !!inlineEditConfig;
  const hasRowActionsColumn = hasInlineEdit || rowActions.length > 0;

  // Infer edit type from column definition
  const inferEditType = (column: ColumnDefinition<T>): 'text' | 'number' | 'checkbox' | 'select' => {
    if (column.editType) return column.editType;
    if (column.editOptions && column.editOptions.length > 0) return 'select';
    if (column.type === 'boolean') return 'checkbox';
    if (column.type === 'number' || column.type === 'currency') return 'number';
    return 'text';
  };

  // Render an editable cell input based on column configuration
  const renderEditCell = (column: ColumnDefinition<T>, editingData: Partial<T>, onFieldChange: (field: keyof T, value: any) => void, originalRow: T) => {
    // Use editingData value if the key exists, otherwise fall back to original row value
    const value = column.key in editingData ? editingData[column.key] : originalRow[column.key];
    const editType = inferEditType(column);
    const inputClasses = "w-full px-1.5 py-0.5 text-xs border border-blue-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white";

    switch (editType) {
      case 'select':
        return (
          <select
            value={String(value ?? '')}
            onChange={(e) => {
              const selectedValue = e.target.value;
              if (selectedValue === '') {
                onFieldChange(column.key, null);
              } else {
                onFieldChange(column.key, column.type === 'number' ? Number(selectedValue) : selectedValue);
              }
            }}
            className={inputClasses}
            onClick={(e) => e.stopPropagation()}
          >
            <option value="">-- Velg --</option>
            {(column.editOptions || []).map(opt => (
              <option key={String(opt.value)} value={String(opt.value)}>{opt.label}</option>
            ))}
          </select>
        );
      case 'checkbox':
        return (
          <div className="flex justify-center">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => onFieldChange(column.key, e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        );
      case 'number':
        return (
          <input
            type="number"
            value={value ?? ''}
            onChange={(e) => onFieldChange(column.key, e.target.value === '' ? null : Number(e.target.value))}
            className={inputClasses}
            onClick={(e) => e.stopPropagation()}
          />
        );
      default: // 'text'
        return (
          <input
            type="text"
            value={String(value ?? '')}
            onChange={(e) => onFieldChange(column.key, e.target.value)}
            className={inputClasses}
            onClick={(e) => e.stopPropagation()}
          />
        );
    }
  };

  // Keyboard handler for inline editing (Escape to cancel, Enter to save)
  useEffect(() => {
    if (!inlineEditConfig || inlineEditConfig.editingRowId === null) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        inlineEditConfig.onEditCancel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [inlineEditConfig?.editingRowId, inlineEditConfig?.onEditCancel]);

  // Render sort icon - white/light icons for dark header
  const renderSortIcon = (columnKey: keyof T) => {
    const { column: sortColumn, direction } = tableState.sort;

    if (sortColumn !== columnKey) {
      return <ChevronsUpDown className="w-3 h-3 text-blue-200" />;
    }

    if (direction === 'asc') {
      return <ChevronUp className="w-3 h-3 text-white" />;
    } else if (direction === 'desc') {
      return <ChevronDown className="w-3 h-3 text-white" />;
    } else {
      return <ChevronsUpDown className="w-3 h-3 text-blue-200" />;
    }
  };

  // Format cell value
  const formatCellValue = (value: any, column: ColumnDefinition<T>, row: T): string => {
    if (column.formatter) {
      return column.formatter(value, row);
    }
    return defaultFormatter(value, column.type);
  };

  // Handle column filter changes
  const handleColumnFilterChange = (columnKey: string, value: string) => {
    const newFilters = {
      ...tableState.filters.columnFilters,
      [columnKey]: value
    };
    handleFilter({ columnFilters: newFilters });
  };

  // Handle dropdown filter changes
  // When values is empty, remove the key entirely so the filter is fully cleared
  const handleDropdownFilterChange = (columnKey: string, values: string[]) => {
    const newFilters = { ...tableState.filters.dropdownFilters };
    if (values.length > 0) {
      newFilters[columnKey] = values;
    } else {
      delete newFilters[columnKey];
    }
    handleFilter({ dropdownFilters: newFilters });
  };

  // Get distinct values for dropdown filters
  const getColumnDistinctValues = (columnKey: keyof T) => {
    return getDistinctValues(tableState.filteredData, columnKey);
  };

  if (loading) {
    return (
      <div className="warehouse-table-loading">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-500 mx-auto mb-2" />
        <p>Laster data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="warehouse-table-empty">
        <AlertCircle className="w-8 h-8 text-red-300 mx-auto mb-2" />
        <h3>Feil ved lasting av data</h3>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Header with controls - Gradient toolbar */}
      {(config.showGlobalSearch || config.showExport || config.showPrint || config.showColumnConfig || tableActions.length > 0 || toolbarLeft) && (
        <div className="sticky top-0 z-20 bg-gradient-to-r from-teal-50 to-blue-50 border-b border-blue-200 px-4 py-3 rounded-t-lg shadow-sm">
          <div className="flex items-center justify-start gap-2 flex-wrap">
              {toolbarLeft}

              {/* Global Search */}
              {config.showGlobalSearch && (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-blue-500" />
                  <input
                    type="text"
                    value={tableState.filters.globalSearch || ''}
                    onChange={(e) => handleFilter({ globalSearch: e.target.value })}
                    placeholder="Søk..."
                    className="pl-9 pr-3 py-2 text-sm border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm transition-all duration-200"
                    style={{ width: '300px' }}
                  />
                </div>
              )}

              {/* Table Actions */}
              {tableActions.map((action) => (
                <button
                  key={action.id}
                  onClick={() => action.onClick(tableState.filteredData, [])}
                  disabled={action.disabled?.(tableState.filteredData, [])}
                  className="px-3 py-2 text-xs font-semibold text-blue-700 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                  {action.icon && <action.icon className="w-4 h-4" />}
                  {action.label}
                </button>
              ))}

              {/* Print Table Button */}
              {config.showPrint && (
                <button
                  onClick={handlePrint}
                  disabled={isPrinting || tableState.filteredData.length === 0}
                  className="px-3 py-2 text-xs font-semibold text-blue-700 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                  <Printer className="w-4 h-4" />
                  {isPrinting ? 'Genererer...' : 'Skriv ut'}
                </button>
              )}

              {/* Export Button */}
              {config.showExport && (
                <button
                  onClick={handleExport}
                  className="px-3 py-2 text-xs font-semibold text-blue-700 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 hover:shadow-md transition-all duration-200 flex items-center gap-1.5"
                >
                  <Download className="w-4 h-4" />
                  Eksporter
                </button>
              )}

              {/* Column Config Button */}
              {config.showColumnConfig && (
                <button
                  onClick={() => setShowColumnConfig(true)}
                  className="px-3 py-2 text-xs font-semibold text-blue-700 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 hover:shadow-md transition-all duration-200 flex items-center gap-1.5"
                >
                  <Settings className="w-4 h-4" />
                  Kolonner
                </button>
              )}
          </div>
        </div>
      )}

      {/* Table Container */}
      <div className="warehouse-table-container flex-1">
        <table
          className={`warehouse-table ${config.compactMode ? 'compact-mode' : ''}`}
          aria-label={ariaLabel}
        >
          <thead className={config.stickyHeader ? 'sticky top-0' : ''}>
            {/* Column Headers */}
            <tr>
              {/* Expand Column Header (for expandable rows) */}
              {expandableConfig && (
                <th className="px-2 py-1.5 w-10 text-center text-xs font-semibold text-white border-r border-blue-400">
                  {/* Empty header for expand column */}
                </th>
              )}
              {/* Selection Column Header */}
              {config.allowRowSelection && (
                <th className="px-2 py-1.5 w-10 text-center text-xs font-semibold text-white border-r border-blue-400">
                  <SelectionCheckbox
                    checked={tableState.selection.isSelectAllChecked}
                    indeterminate={tableState.selection.isSelectAllIndeterminate}
                    onChange={handleSelectAll}
                    aria-label="Select all rows"
                  />
                </th>
              )}
              {/* Row Actions Column Header (position 2, after checkbox) */}
              {hasRowActionsColumn && (
                <th className="px-2 py-1.5 w-20 text-center text-xs font-semibold text-white border-r border-blue-400">
                  Handlinger
                </th>
              )}
              {visibleColumns.map((column) => (
                <ResizableHeader
                  key={column.id}
                  column={column}
                  onResize={handleColumnResize}
                  className={`px-2 py-1.5 text-${column.alignment} text-xs font-semibold text-white cursor-pointer transition-all duration-200 border-r border-blue-400`}
                  onClick={column.sortable ? () => handleSort(column.key) : undefined}
                >
                  <div className={`flex items-center gap-1 ${
                    column.alignment === 'center' ? 'justify-center' :
                    column.alignment === 'right' ? 'justify-end' : 'justify-start'
                  }`}>
                    <span className="flex-1">{column.label}</span>
                    <div className="flex items-center gap-0.5">
                      {/* DISABLED by default - enable via enableDropdownFilters prop */}
                      {enableDropdownFilters && (column.filterType === 'dropdown' || column.filterType === 'both') && (
                        <ColumnDropdownFilter
                          column={column}
                          data={dropdownOptionsData || tableState.data}
                          selectedValues={tableState.filters.dropdownFilters?.[String(column.key)] || []}
                          onSelectionChange={(values) => handleDropdownFilterChange(String(column.key), values)}
                          formatValue={column.formatter}
                          onTextFilterSet={handleColumnFilterChange}
                          textFilterValue={tableState.filters.columnFilters?.[String(column.key)] || ''}
                        />
                      )}
                      {column.sortable && renderSortIcon(column.key)}
                    </div>
                  </div>
                </ResizableHeader>
              ))}
            </tr>

            {/* Filter Row - Only text input fields */}
            {config.showFilters && (
              <tr>
                {/* Empty cell for expand column in filter row */}
                {expandableConfig && (
                  <th className="px-2 py-0.5 w-10 border-r border-blue-400">
                  </th>
                )}
                {/* Empty cell for selection column */}
                {config.allowRowSelection && (
                  <th className="px-2 py-0.5 w-10 border-r border-blue-400">
                  </th>
                )}
                {/* Empty cell for actions column in filter row */}
                {hasRowActionsColumn && (
                  <th className="px-2 py-0.5 w-20 border-r border-blue-400">
                  </th>
                )}
                {visibleColumns.map((column) => (
                  <th key={`filter-${column.id}`} className="px-1 py-0.5 border-r border-blue-400">
                    {(column.filterType === 'date' || column.type === 'date') ? (
                      <DateFilterInput
                        columnKey={String(column.key)}
                        value={tableState.filters.columnFilters?.[String(column.key)] || ''}
                        onChange={handleColumnFilterChange}
                        minWidth={column.width - 4}
                      />
                    ) : (
                      <input
                        type="text"
                        placeholder="..."
                        value={tableState.filters.columnFilters?.[String(column.key)] || ''}
                        onChange={(e) => handleColumnFilterChange(String(column.key), e.target.value)}
                        className="warehouse-table-filter-input"
                        style={{ width: '100%', minWidth: `${column.width - 4}px` }}
                      />
                    )}
                  </th>
                ))}
              </tr>
            )}
          </thead>

          <tbody>
            {tableState.paginatedData.length > 0 ? (
              tableState.paginatedData.map((item, idx) => {
                const rowId = (getRowId || ((row: T) => row.id))(item);
                const isExpanded = expandableConfig && expandedRows?.has(rowId);

                // Calculate colspan for detail row (all visible columns + optional columns)
                const detailColSpan = visibleColumns.length
                  + (expandableConfig ? 1 : 0)
                  + (config.allowRowSelection ? 1 : 0)
                  + (hasRowActionsColumn ? 1 : 0);

                return (
                  <React.Fragment key={`row-${rowId}-${idx}`}>
                    {/* Main Data Row */}
                    {(() => {
                      const isEditingThisRow = hasInlineEdit && rowId === inlineEditConfig!.editingRowId;
                      return (
                        <tr
                          className={`transition-colors duration-150 ${
                            isEditingThisRow ? 'bg-yellow-50 ring-1 ring-inset ring-yellow-200' :
                            isExpanded ? 'bg-blue-50' : ''
                          }`}
                          onClick={(e) => {
                            // Handle expand on row click if configured
                            if (expandableConfig?.expandOnRowClick && handleToggleExpand) {
                              handleToggleExpand(rowId);
                            }
                            actions.onRowClick?.(item, idx);
                          }}
                          onDoubleClick={() => actions.onRowDoubleClick?.(item, idx)}
                          style={{ cursor: actions.onRowClick || expandableConfig?.expandOnRowClick ? 'pointer' : 'default' }}
                        >
                          {/* Expand Column Cell */}
                          {expandableConfig && (
                            <td className="px-2 py-1 w-10 text-center border-r border-slate-200">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleExpand?.(rowId);
                                }}
                                className="p-0.5 rounded hover:bg-blue-100 transition-colors"
                                aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                              >
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4 text-blue-600" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-slate-500" />
                                )}
                              </button>
                            </td>
                          )}
                          {/* Selection Column Cell */}
                          {config.allowRowSelection && (
                            <td className="px-2 py-1 w-10 text-center border-r border-slate-200">
                              <SelectionCheckbox
                                checked={tableState.selection.selectedRows.has(rowId)}
                                onChange={(checked) => handleRowSelect(rowId, checked)}
                                disabled={isEditingThisRow}
                                aria-label={`Select row ${idx + 1}`}
                              />
                            </td>
                          )}
                          {/* Row Actions Column Cell (position 2, after checkbox) */}
                          {hasRowActionsColumn && (
                            <td className="px-2 py-1 w-20 text-center border-r border-slate-200">
                              <div className="flex items-center gap-1 justify-center">
                                {/* Inline edit actions */}
                                {hasInlineEdit && (
                                  isEditingThisRow ? (
                                    <>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          inlineEditConfig!.onEditSave(item, inlineEditConfig!.editingData);
                                        }}
                                        disabled={inlineEditConfig!.isSaving}
                                        className="p-1 text-green-600 hover:text-green-800 hover:bg-green-100 rounded transition-colors disabled:opacity-50"
                                        title="Lagre"
                                      >
                                        <Save className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          inlineEditConfig!.onEditCancel();
                                        }}
                                        className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
                                        title="Avbryt"
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                    </>
                                  ) : (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        inlineEditConfig!.onEditStart(item);
                                      }}
                                      disabled={inlineEditConfig!.editingRowId !== null}
                                      className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded transition-colors disabled:opacity-30"
                                      title="Rediger"
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </button>
                                  )
                                )}
                                {/* Custom row actions */}
                                {rowActions
                                  .filter(action => !action.disabled?.(item))
                                  .map(action => (
                                    <button
                                      key={action.id}
                                      onClick={(e) => { e.stopPropagation(); action.onClick(item, idx); }}
                                      className={`p-1 rounded transition-colors ${
                                        action.variant === 'danger' ? 'text-red-600 hover:bg-red-100' :
                                        action.variant === 'primary' ? 'text-blue-600 hover:bg-blue-100' :
                                        'text-gray-600 hover:bg-gray-100'
                                      }`}
                                      title={action.label}
                                    >
                                      {action.icon && <action.icon className="w-4 h-4" />}
                                    </button>
                                  ))}
                              </div>
                            </td>
                          )}
                          {visibleColumns.map((column) => {
                            const isEditableCell = isEditingThisRow && column.editable;
                            return (
                              <td
                                key={`${rowId}-${column.id}`}
                                className={`px-2 py-1 text-xs border-r border-slate-200 ${
                                  isEditableCell ? '' : 'whitespace-nowrap'
                                } ${
                                  column.alignment === 'center' ? 'text-center' :
                                  column.alignment === 'right' ? 'text-right' : 'text-left'
                                }`}
                                style={{ width: `${column.width}px` }}
                                title={!isEditableCell && (column.key === 'description' || column.type === 'text') ? String(item[column.key] || '') : undefined}
                              >
                                {isEditableCell
                                  ? renderEditCell(column, inlineEditConfig!.editingData, inlineEditConfig!.onEditFieldChange, item)
                                  : column.renderer
                                    ? column.renderer(item[column.key], item)
                                    : formatCellValue(item[column.key], column, item)
                                }
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })()}

                    {/* Detail Row (only rendered when expanded) */}
                    {expandableConfig && isExpanded && (
                      <tr className="bg-slate-50">
                        <td colSpan={detailColSpan} className="p-0">
                          {expandableConfig.renderDetail ? (
                            // Custom render function
                            expandableConfig.renderDetail(item, expandableConfig.getDetailData(item))
                          ) : expandableConfig.detailColumns ? (
                            // Use ExpandableDetailTable with provided columns
                            <ExpandableDetailTable
                              data={expandableConfig.getDetailData(item)}
                              columns={expandableConfig.detailColumns}
                            />
                          ) : (
                            // Fallback if no render function or columns provided
                            <div className="px-4 py-2 text-xs text-slate-500 italic">
                              Ingen detaljer konfigurert
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={visibleColumns.length + (config.allowRowSelection ? 1 : 0) + (expandableConfig ? 1 : 0) + (hasRowActionsColumn ? 1 : 0)} className="warehouse-table-empty">
                  <Search className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <h3>Ingen rader matcher filtrene</h3>
                  <p>Prøv å justere filtrene dine eller slette innhold i kolonnefilterene</p>
                </td>
              </tr>
            )}
          </tbody>

          {/* Summary Footer */}
          {config.showSummary && summaryCalculations.length > 0 && (
            <tfoot className={config.stickyFooter ? 'sticky bottom-0' : ''}>
              <tr>
                {/* Empty cell for expand column in summary */}
                {expandableConfig && (
                  <td className="px-2 py-1 w-10 text-xs font-semibold border-r border-blue-400">
                  </td>
                )}
                {/* Empty cell for selection column in summary */}
                {config.allowRowSelection && (
                  <td className="px-2 py-1 w-10 text-xs font-semibold border-r border-blue-400">
                  </td>
                )}
                {/* Empty cell for actions column in summary */}
                {hasRowActionsColumn && (
                  <td className="px-2 py-1 w-20 text-xs font-semibold border-r border-blue-400">
                  </td>
                )}
                {visibleColumns.map((column) => {
                  const summaryValue = summaryData[String(column.key)];
                  const summaryConfig = summaryCalculations.find(calc => calc.column === column.key);

                  return (
                    <td
                      key={`summary-${column.id}`}
                      className={`px-2 py-1 text-xs font-semibold border-r border-blue-400 ${
                        column.alignment === 'center' ? 'text-center' :
                        column.alignment === 'right' ? 'text-right' : 'text-left'
                      } text-white`}
                    >
                      {summaryValue !== undefined ? (
                        summaryConfig?.formatter ?
                          summaryConfig.formatter(summaryValue) :
                          defaultFormatter(summaryValue, column.type)
                      ) : ''}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Pagination - Gradient design */}
      {config.showPagination && (
        <div className="sticky bottom-0 bg-gradient-to-r from-teal-50 to-blue-50 border-t border-blue-200 px-4 py-3 rounded-b-lg shadow-sm">
          <div className="flex items-center justify-between text-xs text-blue-800 font-medium">
            <div className="flex items-center gap-4">
              <span className="text-blue-900">
                Viser {((tableState.pagination.currentPage - 1) * tableState.pagination.pageSize) + 1}-{Math.min(tableState.pagination.currentPage * tableState.pagination.pageSize, tableState.pagination.totalRows)} av {tableState.pagination.totalRows} rader
              </span>

              <div className="flex items-center gap-2">
                <span className="text-blue-700">Rader per side:</span>
                <select
                  value={tableState.pagination.pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="px-2 py-1 border border-blue-300 rounded-lg text-xs font-medium bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                >
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  {!serverPagination?.enabled && (
                    <option value={tableState.pagination.totalRows}>Alle</option>
                  )}
                  {serverPagination?.enabled && (
                    <option value={500}>500</option>
                  )}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(1)}
                disabled={tableState.pagination.currentPage === 1}
                className="p-1.5 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-blue-600"
                title="Første side"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => handlePageChange(tableState.pagination.currentPage - 1)}
                disabled={tableState.pagination.currentPage === 1}
                className="p-1.5 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-blue-600"
                title="Forrige side"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <span className="px-3 py-1.5 text-xs font-semibold bg-white border border-blue-300 rounded-lg shadow-sm text-blue-800">
                Side {tableState.pagination.currentPage} av {tableState.pagination.totalPages}
              </span>

              <button
                onClick={() => handlePageChange(tableState.pagination.currentPage + 1)}
                disabled={tableState.pagination.currentPage === tableState.pagination.totalPages}
                className="p-1.5 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-blue-600"
                title="Neste side"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => handlePageChange(tableState.pagination.totalPages)}
                disabled={tableState.pagination.currentPage === tableState.pagination.totalPages}
                className="p-1.5 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-blue-600"
                title="Siste side"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Column Configuration Modal */}
      {showColumnConfig && (
        <ColumnConfigModal
          isOpen={showColumnConfig}
          onClose={() => setShowColumnConfig(false)}
          columns={tableState.columns}
          onToggleVisibility={handleColumnToggle}
          onReorder={handleColumnReorder}
          onResetToDefault={handleReset}
        />
      )}
    </div>
  );
}